import type { ChromeSource } from './types'
import { CATEGORY_ORDER, type ObjectDef } from '../game/objects'
import type { PaletteCategory } from '../game/types'
import { touchUiAvailable } from '../core/touch'
import { paletteEntries, type PaletteEntry } from './palette'
import { drawObjectIcon, drawTerrainIcon } from './draw'
import { CHROME_STYLE } from './viewport'
import './chrome.css'

export interface EditorChromeActions {
  category(category: PaletteCategory): void
  part(entry: PaletteEntry): void
  tool(id: string): void
  undo(): void
  redo(): void
  save(): void
  play(): void
  watch(): void
  close(): void
  theme(): void
  rename(): void
  home(): void
  resize(delta: number): void
  remove(): void
}

/** Native controls stay legible at any display scale and expose real hit targets. */
export class EditorChrome {
  readonly root = document.createElement('section')
  private readonly parts = document.createElement('div')
  private readonly inspector = document.createElement('div')
  private readonly dimensions = document.createElement('span')
  private readonly shrink: HTMLButtonElement
  private readonly stretch: HTMLButtonElement
  /** True where the pan gesture is two fingers; chosen once, not per frame. */
  private readonly touch = touchUiAvailable()
  private readonly status = document.createElement('div')
  private readonly name = document.createElement('button')
  private readonly copyCue = document.createElement('span')
  private readonly theme = document.createElement('button')
  private readonly tabs = new Map<string, HTMLButtonElement>()
  private readonly tools = new Map<string, HTMLButtonElement>()
  private readonly entries = new Map<string, HTMLButtonElement>()
  private readonly undo: HTMLButtonElement
  private readonly redo: HTMLButtonElement
  private category: PaletteCategory | null = null
  private hover: string | null = null
  /** Last part key scrolled into view, so a steady selection is not re-scrolled. */
  private scrolledKey: string | null = null

  constructor(private readonly actions: EditorChromeActions) {
    this.root.className = 'maker-ui'
    this.root.setAttribute('style', CHROME_STYLE)
    this.root.setAttribute('aria-label', 'Course editor')
    const header = document.createElement('header')
    header.className = 'maker-header'
    const brand = document.createElement('strong')
    brand.textContent = 'WORKSHOP'
    this.name.className = 'maker-name'
    this.name.title = 'Rename course (N)'
    this.name.onclick = actions.rename
    this.copyCue.className = 'maker-copy-cue'
    this.copyCue.hidden = true
    this.copyCue.title =
      'Built-in courses cannot be overwritten; Save creates a copy'
    header.append(brand, this.name, this.copyCue)
    this.undo = this.button('↶', 'Undo (Ctrl+Z)', actions.undo)
    this.redo = this.button('↷', 'Redo (Ctrl+Shift+Z)', actions.redo)
    header.append(
      this.undo,
      this.redo,
      this.button('Save', 'Save course', actions.save),
    )
    const play = this.button(
      '▶ Play test',
      'Play this draft from the start',
      actions.play,
    )
    play.className = 'maker-play'
    header.append(
      this.button('Watch bot', 'Watch the bot play this draft', actions.watch),
      play,
      this.button('×', 'Close editor (Esc)', actions.close),
    )

    const shelf = document.createElement('div')
    shelf.className = 'maker-shelf'
    const categories = document.createElement('nav')
    categories.className = 'maker-categories'
    categories.setAttribute('aria-label', 'Part categories')
    const label = document.createElement('span')
    label.textContent = 'PARTS'
    categories.append(label)
    for (const category of CATEGORY_ORDER) {
      const button = this.button(
        category[0]!.toUpperCase() + category.slice(1),
        `Choose ${category} parts`,
        () => actions.category(category),
      )
      categories.append(button)
      this.tabs.set(category, button)
    }
    this.parts.className = 'maker-parts'
    this.parts.setAttribute('aria-label', 'Available parts')
    shelf.append(categories, this.parts)

    const rail = document.createElement('nav')
    rail.className = 'maker-tools'
    rail.setAttribute('aria-label', 'Editing tools')
    const toolsLabel = document.createElement('span')
    toolsLabel.className = 'maker-section-label'
    toolsLabel.textContent = 'TOOLS'
    rail.append(toolsLabel)
    for (const [id, symbol, label, help] of [
      ['paint', '✎', 'Draw', 'Paint the selected part · drag to draw'],
      ['erase', '⌫', 'Erase', 'Erase terrain and objects · drag to erase'],
      ['move', '↖', 'Move', 'Select and drag an object (M)'],
      ['fill', '▧', 'Fill', 'Drag a rectangle of the selected part (F)'],
      [
        'eyedrop',
        '⌖',
        'Pick',
        'Sample a part from the course (Q / right click)',
      ],
      [
        'hand',
        '✥',
        'Pan',
        'Drag to move the view · Alt / middle mouse also pans',
      ],
      ['start', '⚑', 'Start', 'Place Tavi’s starting position (R)'],
    ]) {
      const button = this.button('', help!, () => actions.tool(id!))
      const icon = document.createElement('span')
      icon.className = 'maker-tool-icon'
      icon.textContent = symbol!
      button.append(icon, document.createTextNode(label!))
      rail.append(button)
      this.tools.set(id!, button)
    }
    this.theme.className = 'maker-theme'
    this.theme.title = 'Change course theme'
    this.theme.onclick = actions.theme
    rail.append(
      this.button('⌂ Start', 'Center view on Tavi’s start', actions.home),
      this.theme,
    )
    this.status.className = 'maker-status'
    this.status.setAttribute('role', 'status')
    this.inspector.className = 'maker-inspector'
    this.shrink = this.button('−', 'Shrink selected object', () =>
      actions.resize(-1),
    )
    this.stretch = this.button('+', 'Stretch selected object', () =>
      actions.resize(1),
    )
    this.inspector.append(
      this.dimensions,
      this.shrink,
      this.stretch,
      this.button('Delete', 'Delete selected object', actions.remove),
    )
    this.root.append(header, shelf, rail, this.status, this.inspector)
    document.getElementById('frame')?.append(this.root)
    document.getElementById('stage')?.classList.add('editing')
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation())
    this.root.addEventListener('keydown', (e) => e.stopPropagation())
    this.root.addEventListener('keyup', (e) => e.stopPropagation())
  }

  private button(
    text: string,
    title: string,
    action: () => void,
  ): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = text
    button.title = title
    button.setAttribute('aria-label', title)
    button.onclick = () => {
      action()
      button.blur()
    }
    button.onpointerenter = () => {
      this.hover = title
    }
    button.onpointerleave = () => {
      this.hover = null
    }
    return button
  }

  update(
    s: ChromeSource,
    hand = false,
    selected: ObjectDef | null = null,
  ): void {
    this.inspector.hidden = selected === null
    const canResize = Boolean(selected?.resize)
    this.shrink.hidden = !canResize
    this.stretch.hidden = !canResize
    this.dimensions.textContent = selected
      ? canResize
        ? `${selected.name} · ${selected.w} × ${selected.h}  ↔`
        : `${selected.name} · ${selected.w} × ${selected.h}`
      : ''
    this.inspector.title = canResize
      ? 'Drag the yellow handle to stretch. Move drags the entire object.'
      : 'Delete removes the selected object.'
    this.name.textContent =
      s.editingName === null
        ? `${s.levelName} ✎`
        : `${s.editingName}▏ · Enter to finish`
    this.copyCue.hidden = s.copyAs === null
    this.copyCue.textContent = s.copyAs === null ? '' : `saves as ${s.copyAs}`
    this.theme.textContent = s.theme[0]!.toUpperCase() + s.theme.slice(1)
    this.undo.disabled = s.undoDepth === 0
    this.redo.disabled = s.redoDepth === 0
    if (this.category !== s.activeCategory) {
      this.category = s.activeCategory
      this.scrolledKey = null
      this.parts.replaceChildren()
      this.entries.clear()
      for (const entry of paletteEntries(s.activeCategory).filter(
        (entry) => entry.toolId !== 'erase',
      )) {
        const button = this.button(
          '',
          `${entry.name}${entry.slot ? ` (${entry.slot})` : ''} · ${entry.hint}`,
          () => this.actions.part(entry),
        )
        button.className = 'maker-part'
        button.setAttribute('aria-label', entry.name)
        const canvas = document.createElement('canvas')
        canvas.width = 32
        canvas.height = 32
        const ctx = canvas.getContext('2d')!
        ctx.scale(2, 2)
        if (entry.kind === 'terrain')
          drawTerrainIcon(ctx, entry.terrain, 0, 0, 16, entry.color)
        else if (entry.def)
          drawObjectIcon(ctx, entry.def, entry.code, 0, 0, 16, entry.color)
        const label = document.createElement('span')
        label.textContent = entry.name
        button.append(canvas, label)
        this.parts.append(button)
        this.entries.set(entry.key, button)
      }
    }
    for (const [key, button] of this.tabs)
      button.setAttribute('aria-pressed', String(key === s.activeCategory))
    for (const [key, button] of this.entries) {
      const on = key === s.selectedKey
      button.setAttribute('aria-pressed', String(on))
      if (on && this.scrolledKey !== key) {
        button.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        this.scrolledKey = key
      }
    }
    const tool = s.activeTool.id
    const active =
      (s.activeTool.kind === 'terrain' && tool !== 'erase') || tool === 'object'
        ? 'paint'
        : tool
    for (const [key, button] of this.tools)
      button.setAttribute(
        'aria-pressed',
        String(key === (hand ? 'hand' : active)),
      )
    const warnings = s.warnings.length ? `  ⚠ ${s.warnings[0]}` : ''
    const summary = `${s.activeTool.name} · ${s.brushLabel}`
    // Touch has no Alt key; the gesture that pans there is two fingers.
    const panHint = this.touch ? 'Two-finger drag to pan' : 'Alt + drag to pan'
    this.status.textContent =
      this.hover ??
      `${summary}  —  ${s.activeTool.id === 'object' ? s.selectedHint : s.activeTool.hint}   |   ${panHint}${warnings}`
    this.status.title = s.warnings.join('\n')
  }

  dispose(): void {
    this.root.remove()
    document.getElementById('stage')?.classList.remove('editing')
  }
}
